import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Plus, MoreHorizontal, Edit, Trash2, Search, X } from "lucide-react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { toast } from "sonner";
import { AddEquipmentSheet } from "./AddEquipmentSheet";
import { EditEquipmentSheet } from "./EditEquipmentSheet";
import { MaintenanceLogSheet } from "./MaintenanceLogSheet";
import { formatCurrency } from "@/lib/utils";
import { EmptyState } from "@/components/ui/empty-state";
import { Wrench } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Status options
const STATUS_OPTIONS = [
  { value: "In Service", label: "In Service" },
  { value: "Under Repair", label: "Under Repair" },
  { value: "Retired", label: "Retired" },
];

// Warranty expiration options
const WARRANTY_OPTIONS = [
  { value: "expired", label: "Expired" },
  { value: "expiring_30", label: "Expiring in 30 days" },
  { value: "expiring_90", label: "Expiring in 90 days" },
  { value: "valid", label: "Valid (90+ days)" },
];

import { useAppAuth } from "@/contexts/AuthContext";

export function EquipmentList() {
  const { hasPermission } = useAppAuth();
  const canWrite = hasPermission("clients:write");
  const [isAddEquipmentOpen, setIsAddEquipmentOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState<Id<"equipment"> | null>(null);
  const [maintenanceLogEquipment, setMaintenanceLogEquipment] = useState<Id<"equipment"> | null>(
    null
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [warrantyFilter, setWarrantyFilter] = useState<string | null>(null);
  const [equipmentToDelete, setEquipmentToDelete] = useState<{ id: Id<"equipment">; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const equipment = useQuery(api.equipment.list) || [];
  const deleteEquipment = useMutation(api.equipment.remove);

  // Check if any filters are active
  const hasActiveFilters = statusFilter !== null || warrantyFilter !== null;

  // Clear all filters
  const clearFilters = () => {
    setStatusFilter(null);
    setWarrantyFilter(null);
  };

  // Helper function to check warranty status
  const getWarrantyStatus = (warrantyDate: string | undefined): string => {
    if (!warrantyDate) return "unknown";
    const today = new Date();
    const warranty = new Date(warrantyDate);
    const daysRemaining = Math.ceil((warranty.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (daysRemaining < 0) return "expired";
    if (daysRemaining <= 30) return "expiring_30";
    if (daysRemaining <= 90) return "expiring_90";
    return "valid";
  };

  const handleDelete = async () => {
    if (!equipmentToDelete) return;
    setIsDeleting(true);
    try {
      await deleteEquipment({ id: equipmentToDelete.id });
      toast.success("Equipment deleted successfully");
      setEquipmentToDelete(null);
    } catch (error) {
      toast.error("Failed to delete equipment");
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "In Service":
        return "bg-green-100 text-green-800 border-green-200 hover:bg-green-100";
      case "Under Repair":
        return "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100";
      case "Retired":
        return "bg-red-100 text-red-800 border-red-200 hover:bg-red-100";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-100";
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  // Filter equipment based on search query and filters
  const filteredEquipment = Array.isArray(equipment)
    ? equipment.filter((item) => {
        // Apply search filter
        const matchesSearch =
          searchQuery === "" ||
          item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.model.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (item.serialNumber?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
          (item.location?.locationName?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
          (item.location?.businessName?.toLowerCase() || "").includes(searchQuery.toLowerCase());

        // Apply status filter
        const matchesStatus = statusFilter === null || item.status === statusFilter;

        // Apply warranty filter
        const matchesWarranty =
          warrantyFilter === null ||
          getWarrantyStatus(item.warrantyExpirationDate) === warrantyFilter;

        return matchesSearch && matchesStatus && matchesWarranty;
      })
    : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Equipment</h1>
          <p className="mt-1 text-gray-500">Manage your equipment inventory</p>
        </div>
        {canWrite && (
          <Button className="gap-2" onClick={() => setIsAddEquipmentOpen(true)}>
            <Plus className="h-4 w-4" /> Add Equipment
          </Button>
        )}
      </div>

      <Card className="w-full p-6">
        <div className="mb-6 flex flex-col gap-4 md:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <Input
              placeholder="Search equipment..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Select
            value={statusFilter || "all"}
            onValueChange={(value) => setStatusFilter(value === "all" ? null : value)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATUS_OPTIONS.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={warrantyFilter || "all"}
            onValueChange={(value) => setWarrantyFilter(value === "all" ? null : value)}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by warranty" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Warranties</SelectItem>
              {WARRANTY_OPTIONS.map((warranty) => (
                <SelectItem key={warranty.value} value={warranty.value}>
                  {warranty.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="gap-2 text-gray-500 hover:text-gray-700"
            >
              <X className="h-4 w-4" /> Clear Filters
            </Button>
          )}
        </div>

        <div className="w-full overflow-auto">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Equipment Name</TableHead>
                <TableHead>Brand</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Serial Number</TableHead>
                <TableHead>Purchase Date</TableHead>
                <TableHead>Purchase Price</TableHead>
                <TableHead>Warranty Expiration</TableHead>
                <TableHead>Client Location</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEquipment.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10}>
                    <EmptyState
                      icon={Wrench}
                      title="No equipment yet"
                      description={
                        searchQuery
                          ? "No equipment found matching your search."
                          : "Add your first piece of equipment to get started."
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : (
                filteredEquipment.map((item) => (
                  <TableRow key={item._id}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>{item.brand}</TableCell>
                    <TableCell>{item.model}</TableCell>
                    <TableCell>{item.serialNumber || "-"}</TableCell>
                    <TableCell>{formatDate(item.purchaseDate)}</TableCell>
                    <TableCell>{formatCurrency(item.purchasePrice)}</TableCell>
                    <TableCell>{formatDate(item.warrantyExpirationDate)}</TableCell>
                    <TableCell>
                      {item.location ? (
                        <div className="text-sm">
                          <div className="font-medium">{item.location.locationName}</div>
                          <div className="text-gray-500">{item.location.businessName}</div>
                        </div>
                      ) : (
                        <span className="text-gray-500">Unassigned</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusBadgeClass(item.status)}>{item.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setMaintenanceLogEquipment(item._id)}>
                            <Wrench className="mr-2 h-4 w-4" />
                            Maintenance Logs
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setEditingEquipment(item._id)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setEquipmentToDelete({ id: item._id, name: item.name })}
                            className="text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <AddEquipmentSheet isOpen={isAddEquipmentOpen} onClose={() => setIsAddEquipmentOpen(false)} />

      {editingEquipment && (
        <EditEquipmentSheet
          equipmentId={editingEquipment}
          isOpen={!!editingEquipment}
          onClose={() => setEditingEquipment(null)}
        />
      )}

      {maintenanceLogEquipment && (
        <MaintenanceLogSheet
          equipmentId={maintenanceLogEquipment}
          isOpen={!!maintenanceLogEquipment}
          onClose={() => setMaintenanceLogEquipment(null)}
        />
      )}

      <AlertDialog
        open={!!equipmentToDelete}
        onOpenChange={(open) => !open && setEquipmentToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete {equipmentToDelete?.name} and remove it from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
